import { KeyboardBackspace } from '@mui/icons-material';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import keys from 'lodash/keys';
import { ReactElement, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import Grid from '@mui/material/Grid';

// Components.
import { ReportGroup } from './ReportGroup';

// Hooks.
import { useLoadEvent } from 'hooks';
import { getReportsByWorkspaceId, getSmsSummaryReportAuthorization } from 'store/report/actions';
import {
  isLoadingReportsSelector,
  isSmsSummaryReportAuthorizedSelector,
  reportsSelector,
} from 'store/report/selectors';
import { ReportGroups } from 'store/report/types';
import { currentOrganizationContextSelector } from 'store/user/selectors';

// Store + Core
import { PARSAction, RollupOrganizationEnums } from 'core/models';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from 'store';

// Styles.
import styles from './ReportPage.module.scss';

export const ReportPage = (): ReactElement => {
  const dispatch: ThunkDispatch<AppState, unknown, PARSAction> = useDispatch();
  const currentOrganization = useSelector(currentOrganizationContextSelector);
  const isLoadingReports = useSelector(isLoadingReportsSelector);
  const reports = useSelector(reportsSelector);
  const isSmsSummaryReportAuthorized = useSelector(isSmsSummaryReportAuthorizedSelector);
  const reportGroups = useMemo(() => groupBy(reports, (item) => get(item, 'group', 'Reports')), [reports]);
  const isNars = currentOrganization.rollupOrganizationEnum === RollupOrganizationEnums.NARS;

  const fireLoadEvent = useLoadEvent({ PageName: 'Report Dashboard', PageType: 'Report' });

  useEffect(() => {
    fireLoadEvent();
  }, [fireLoadEvent]);

  useEffect(() => {
    if (!reports && currentOrganization) {
      dispatch(
        getReportsByWorkspaceId(
          currentOrganization.businessId,
          currentOrganization.organizationKind,
          currentOrganization.rollupOrganizationEnum,
        ),
      );
    }
  }, [dispatch, reports, currentOrganization]);

  useEffect(() => {
    if (!isNars) dispatch(getSmsSummaryReportAuthorization());
  }, []);

  return (
    <div className={styles.container}>
      <Grid container spacing={2}>
        <Grid size={{ md: 2 }}>
          <Link className={styles.link} to="/dashboard">
            <KeyboardBackspace /> Dashboard
          </Link>
        </Grid>
        {!isNars && (
          <Grid size={{ md: 10 }}>
            {isSmsSummaryReportAuthorized && (
              <Link className={styles.link} to="/reports/summaryreports">
                SMS Summary Provider Review
              </Link>
            )}
          </Grid>
        )}
      </Grid>
      {reports?.length > 0 ? (
        keys(reportGroups).map((group, index) => (
          <ReportGroup group={group as ReportGroups} key={index} reports={reportGroups[group]} />
        ))
      ) : (
        <ReportGroup isLoading={isLoadingReports} />
      )}
    </div>
  );
};
